import clsx from "clsx";
import { AddonDraft } from "./billing-config-draft";

/** Subtle bordered card container */
export function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-surface-tertiary border border-border-secondary rounded-xl p-5">
      {children}
    </div>
  );
}

/** A single row inside a Card */
export function CardRow({
  icon,
  label,
  value,
  indent,
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  indent?: boolean;
}) {
  return (
    <div
      className={clsx(
        "flex items-center justify-between py-2",
        indent && "pl-[26px]",
      )}
    >
      <span className="flex items-center gap-2 text-sm text-content-tertiary shrink-0">
        {icon}
        {label}
      </span>
      <span className="text-sm text-content-primary text-right truncate ml-4">
        {value}
      </span>
    </div>
  );
}

/** Key-value item for the billing config grid */
export function ConfigItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs text-content-tertiary uppercase tracking-wider mb-1">
        {label}
      </div>
      <div className="text-base font-medium text-content-primary">{value}</div>
    </div>
  );
}

/** A small toggle switch styled to match the modal's other toggles. */
export function ToggleSwitch({
  checked,
  ariaLabel,
  disabled,
  onToggle,
}: {
  checked: boolean;
  ariaLabel: string;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onToggle}
      className="flex items-center cursor-pointer select-none"
    >
      <div
        className={clsx(
          "w-8 h-[18px] rounded-full relative transition-colors",
          checked ? "bg-primary" : "bg-content-disabled",
        )}
      >
        <div
          className={clsx(
            "absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform",
            checked ? "translate-x-[16px]" : "translate-x-[2px]",
          )}
        />
      </div>
    </button>
  );
}

/** Edit-mode controls for a single add-on: enable toggle, monthly price, start
 *  date (anchors proration + trial), and a 30-day trial toggle. */
export function AddonEditor({
  draft,
  disabled,
  onChange,
}: {
  draft: AddonDraft;
  disabled: boolean;
  onChange: (patch: Partial<AddonDraft>) => void;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ToggleSwitch
          checked={draft.enabled}
          ariaLabel={`Enable ${draft.displayName}`}
          disabled={disabled}
          onToggle={() => onChange({ enabled: !draft.enabled })}
        />
        <span className="text-sm text-content-secondary">
          Enable {draft.displayName}
        </span>
      </div>

      {draft.enabled && (
        <div className="space-y-3 pl-10">
          <div>
            <label className="block text-xs font-medium text-content-tertiary mb-1">
              Monthly Price ($)
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max="50000"
              value={draft.monthlyPrice}
              onChange={(e) => onChange({ monthlyPrice: e.target.value })}
              placeholder="e.g. 500"
              className="w-full px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
              disabled={disabled}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-content-tertiary mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={draft.startDate}
              onChange={(e) => onChange({ startDate: e.target.value })}
              className="w-full px-3 py-2 text-sm border border-border-primary rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary bg-surface-primary"
              disabled={disabled}
            />
            <p className="text-xs text-content-tertiary mt-1">
              Anchors first-month proration. The add-on does not bill until this
              is set.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <ToggleSwitch
              checked={draft.trialEnabled}
              ariaLabel={`Enable 30-day trial for ${draft.displayName}`}
              disabled={disabled}
              onToggle={() => onChange({ trialEnabled: !draft.trialEnabled })}
            />
            <span className="text-sm text-content-secondary">
              30-day trial (first 30 days waived)
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
